import { useMutation, useQueryClient } from "@tanstack/react-query";
import { t } from "i18next";

import { mutateData } from "@/core/api/api-methods";
import {
  apiErrorHandler,
  getBackendErrorMessage,
} from "@/core/api/api-error-handler";
import {
  invalidateAfterMutation,
  kycCaseCacheKeys,
} from "@/core/api/invalidate-after-mutation";
import { logger } from "@/core/utils/func/logger";
import { kycEndpoints } from "@/features/kyc/api/endpoints";
import type { KycDocumentDto } from "@/features/kyc/api/dto";

export interface ReviewKycDocumentParams {
  kycCaseId: string;
  documentId: string;
  decision: "VALIDATED" | "REJECTED";
  /** OBLIGATOIRE si decision = REJECTED (guide §6.2 : un rejet doit être motivé). */
  reason?: string;
}

/**
 * Valide ou rejette une pièce justificative
 * (`POST .../documents/{id}/review?decision=…&reason=…` — paramètres en
 * query, corps vide). Rôles flashpay-superadmin / flashpay-admin.
 */
export const useReviewKycDocument = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["kyc", "kyc-documents", "review"],
    mutationFn: ({
      kycCaseId,
      documentId,
      decision,
      reason,
    }: ReviewKycDocumentParams) => {
      if (decision === "REJECTED" && !reason?.trim()) {
        throw new Error(t("requiredFieldError"));
      }
      const params = new URLSearchParams({ decision });
      if (reason?.trim()) params.set("reason", reason.trim());
      return mutateData({
        url: `${kycEndpoints.kycDocuments.review(kycCaseId, documentId)}?${params.toString()}`,
        method: "POST",
      }) as Promise<KycDocumentDto>;
    },
    onSuccess: (_data, { kycCaseId }) => {
      return invalidateAfterMutation(queryClient, kycCaseCacheKeys(kycCaseId));
    },
    onError: (error) => {
      const message = getBackendErrorMessage(error) ?? apiErrorHandler(error);
      logger.error(
        `useReviewKycDocument failed: ${message}`,
        error instanceof Error ? error : undefined,
        { prefix: "kyc" }
      );
    },
  });
};
